import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {container, displayHeading, down, eyebrow, focusRing} from "./media";

/**
 * Editorial kit — the layout vocabulary shared by the content pages (Studio,
 * About, Contact) so they read as one publication instead of separately
 * styled screens.
 */

const Page = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding-bottom: clamp(56px, 8vw, 110px);
`

const Section = styled.section`
    ${container};
    padding-top: clamp(48px, 7vw, 104px);

    ${down('md')} {
        padding-top: 42px;
    }
`

const Head = styled.div`
    ${container};
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-width: min(1320px, 100%);
    padding-top: clamp(20px, 2.6vw, 40px);
`

const Eyebrow = styled.span`
    ${eyebrow};
    display: inline-flex;
    align-items: center;
    gap: 10px;

    &::before {
        content: '';
        width: 22px;
        height: 1px;
        background-color: ${({theme}) => theme.accent};
    }
`

const PageTitle = styled.h1`
    ${displayHeading};
    max-width: 22ch;
    font-size: clamp(34px, 6vw, 80px);
`

const SectionTitle = styled.h2`
    ${displayHeading};
    font-size: clamp(26px, 3.4vw, 44px);
`

const Lead = styled.p`
    max-width: 62ch;
    font-size: clamp(16px, 1.6vw, 20px);
    line-height: 1.6;
    color: ${({theme}) => theme.colorMuted};
`

const Body = styled.p`
    max-width: 68ch;
    font-size: 15.5px;
    line-height: 1.75;
    white-space: pre-line;
    color: ${({theme}) => theme.colorMuted};
`

/** Two column layout: a heading that sticks while its text scrolls. */
const Split = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
    gap: clamp(20px, 4vw, 72px);
    padding: clamp(26px, 4vw, 44px) 0;
    border-top: 1px solid ${({theme}) => theme.lineFaint};

    ${down('md')} {
        grid-template-columns: minmax(0, 1fr);
        gap: 14px;
    }
`

const SplitHeading = styled.div`
    position: sticky;
    top: calc(${({theme}) => theme.header.height}px + 28px);
    display: flex;
    align-items: flex-start;
    gap: 14px;
    align-self: start;

    ${down('md')} {
        position: static;
    }
`

const SplitIndex = styled.span`
    font-family: ${({theme}) => theme.font.mono};
    font-size: 12px;
    letter-spacing: 0.1em;
    padding-top: 7px;
    color: ${({theme}) => theme.accent};
`

const SplitTitle = styled.h2`
    font-size: clamp(20px, 2.4vw, 30px);
    letter-spacing: -0.02em;
    color: ${({theme}) => theme.color};
`

/* -------------------------------------------------------------------- cards */

const CardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(268px, 100%), 1fr));
    gap: clamp(12px, 1.6vw, 20px);
`

const Card = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: clamp(20px, 2.6vw, 30px);
    border: 1px solid ${({theme}) => theme.line};
    border-radius: ${({theme}) => theme.radius.md};
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.012));
`

const CardIcon = styled(FontAwesomeIcon)`
    width: 18px;
    height: 18px;
    margin-bottom: 4px;
    color: ${({theme}) => theme.accent};
`

const CardTitle = styled.h3`
    font-size: 19px;
    letter-spacing: -0.02em;
    color: ${({theme}) => theme.color};
`

const CardText = styled.p`
    font-size: 14.5px;
    line-height: 1.65;
    white-space: pre-line;
    color: ${({theme}) => theme.colorMuted};
`

/* --------------------------------------------------------------------- chips */

const ChipRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`

const Chip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    border: 1px solid ${({theme}) => theme.line};
    border-radius: ${({theme}) => theme.radius.pill};
    font-size: 12.5px;
    font-weight: 600;
    color: ${({theme}) => theme.colorMuted};
`

const ChipIcon = styled(FontAwesomeIcon)`
    width: 12px;
    height: 12px;
    color: ${({theme}) => theme.accent};
`

/* ----------------------------------------------------------------- link card */

const LinkCard = styled.a`
    ${focusRing};
    display: flex;
    align-items: center;
    gap: 16px;
    padding: clamp(18px, 2.4vw, 26px);
    border: 1px solid ${({theme}) => theme.line};
    border-radius: ${({theme}) => theme.radius.md};
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.012));
    transition: border-color 240ms ${({theme}) => theme.ease.out},
    background-color 240ms ${({theme}) => theme.ease.out},
    transform 240ms ${({theme}) => theme.ease.out};

    &:hover {
        border-color: ${({theme}) => theme.accentLine};
        transform: translateY(-3px);
    }
`

const LinkCardIconRing = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    flex-shrink: 0;
    border: 1px solid ${({theme}) => theme.line};
    border-radius: 50%;
    background-color: ${({theme}) => theme.accentSoft};
`

const LinkCardIcon = styled(FontAwesomeIcon)`
    width: 16px;
    height: 16px;
    color: ${({theme}) => theme.accent};
`

const LinkCardText = styled.span`
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
`

const LinkCardLabel = styled.span`
    ${eyebrow};
    font-size: 10px;
`

const LinkCardValue = styled.span`
    font-size: 17px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: ${({theme}) => theme.color};
    overflow-wrap: anywhere;
`

const LinkCardArrow = styled(FontAwesomeIcon)`
    width: 13px;
    height: 13px;
    margin-left: auto;
    flex-shrink: 0;
    color: ${({theme}) => theme.colorFaint};
    transition: transform 240ms ${({theme}) => theme.ease.out}, color 240ms ${({theme}) => theme.ease.out};

    ${LinkCard}:hover & {
        transform: translate(3px, -3px);
        color: ${({theme}) => theme.accent};
    }
`

/* ----------------------------------------------------------------- cta panel */

const CtaPanel = styled.div`
    display: flex;
    align-items: center;
    gap: clamp(24px, 4vw, 56px);
    padding: clamp(28px, 4.6vw, 60px);
    overflow: hidden;
    border: 1px solid ${({theme}) => theme.line};
    border-radius: ${({theme}) => theme.radius.xl};
    background: radial-gradient(120% 140% at 100% 0%, rgba(214, 218, 225, 0.10), transparent 60%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.015));

    ${down('md')} {
        flex-direction: column;
        align-items: flex-start;
    }
`

const CtaText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`

const CtaActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-left: auto;

    ${down('md')} {
        margin-left: 0;
        width: 100%;
    }

    ${down('sm')} {
        flex-direction: column;
        align-items: stretch;
    }
`

const InlineLink = styled.a`
    font-weight: 700;
    color: ${({theme}) => theme.accent};
    text-decoration: none;
    border-bottom: 1px solid ${({theme}) => theme.accentLine};
    transition: color 200ms ${({theme}) => theme.ease.out}, border-color 200ms ${({theme}) => theme.ease.out};

    &:hover {
        color: ${({theme}) => theme.accentHover};
        border-color: ${({theme}) => theme.accentHover};
    }
`

export const Editorial = {
    Page,
    Section,
    Head,
    Eyebrow,
    PageTitle,
    SectionTitle,
    Lead,
    Body,

    Split,
    SplitHeading,
    SplitIndex,
    SplitTitle,

    CardGrid,
    Card,
    CardIcon,
    CardTitle,
    CardText,

    ChipRow,
    Chip,
    ChipIcon,

    LinkCard,
    LinkCardIconRing,
    LinkCardIcon,
    LinkCardText,
    LinkCardLabel,
    LinkCardValue,
    LinkCardArrow,

    CtaPanel,
    CtaText,
    CtaActions,
    InlineLink
}

export default Editorial
